import { SearchPageBase } from '@common/SearchPageBase';
import { SearchFilter } from '@common/searchFilter/SearchFilter';
import { Accordion, Grid, Paginator } from '@gcl/react-core';
import { routes } from '@routes/routes.models';
import { useNavigate } from '@routes/routes.hooks';
import { IFilterFieldConfig, SearchFilterType } from '@common/searchFilter/searchFilter.models';
import { useSearchFilterUrlParam } from '@common/searchFilter/searchFilterUrlParam.hooks';
import { getSelectFilterFieldNameId } from '@common/searchFilter/searchFilter.services';
import { NumberParam, StringParam } from 'use-query-params';
import React from 'react';
import { useFetchDesignerCompanyOptions } from '@company/projectCompany/projectCompany.queries';
import { useProjectId } from '@config/config.hooks';
import { ISortByQuery } from '@common/api.models';
import { useFetchDrawingRegister } from './projectDrawingRegister.queries';
import { EProjectDrawingRegisterSearchKey } from './projectDrawingRegister.constants';
import { IProjectDrawingRegisterFilter } from './projectDrawingRegister.models';
import './DocumentRegisterList.scss';
import { createTableLinkWithOpenNewTabElement } from '@common/tableColumn.services';
import { useUserProfilePreferencesPageSize } from '@userProfile/userProfilePreferences.hooks';
import { EUserProfilePreferenceAttributeName } from '@userProfile/userProfilePreferences.constants';
import { DEFAULT_TABLE_PAGE_SIZE_OPTIONS } from '@common/constants';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { DocumentRegisterT1AccordionContent } from './DocumentRegisterT1AccordionContent';

export const DocumentRegisterList = () => {
  const { navigate, generateRoutePath, projectNo } = useNavigate();
  const projectId = useProjectId();
  const { initPageSize, pageSize, setPageSize } = useUserProfilePreferencesPageSize(
    EUserProfilePreferenceAttributeName.DOCUMENT_REGISTER_LIST_PAGE_SIZE,
  );

  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [sortByQuery, setSortByQuery] = React.useState<ISortByQuery | undefined>(undefined);

  // #region Search Field Configs
  const drawingRegisterSearchFieldConfigs = React.useMemo(() => {
    return [
      {
        fieldName: EProjectDrawingRegisterSearchKey.KEYWORD,
        displayName: 'Keyword',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      {
        fieldName: EProjectDrawingRegisterSearchKey.DESIGNER,
        displayName: 'Designer',
        type: SearchFilterType.Select,
        useFetchOptions: useFetchDesignerCompanyOptions({}),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
    ];
  }, []);
  // #endregion Search Field Configs

  const { localFilter, setLocalFilter } = useSearchFilterUrlParam(
    {
      [EProjectDrawingRegisterSearchKey.KEYWORD]: StringParam,
      [`${getSelectFilterFieldNameId(EProjectDrawingRegisterSearchKey.DESIGNER)}`]: NumberParam,
    },
    drawingRegisterSearchFieldConfigs,
  );
  const {
    data: dataDrawingRegister,
    isLoading: isLoadingDrawingRegister,
    isRefetching: isRefetchingDrawingRegister,
    error: errorDrawingRegister,
  } = useFetchDrawingRegister({
    pageSize,
    pageNumber,
    projectId,
    ...(sortByQuery || {
      sortByField: 'refNo',
      isDesc: false,
    }),
    filter: Object.keys(localFilter).length > 0 ? (localFilter as IProjectDrawingRegisterFilter) : undefined,
  });

  const getValidPageIndex = (pageIdx: number, totalPageCount: number): number => {
    if (pageIdx <= 1) return 1;
    if (pageIdx > totalPageCount) return totalPageCount;
    return pageIdx;
  };

  return (
    <SearchPageBase
      data={dataDrawingRegister?.items}
      breadcrumbItems={[
        {
          text: `${projectNo} - Home`,
          prefixIconName: 'House',
          onClick: () => {
            navigate(routes.t1.path);
          },
        },
        {
          text: `Search Drawing Register`,
          prefixIconName: 'Search',
          active: true,
        },
      ]}
      title="Document Register"
      isTableRefetching={isRefetchingDrawingRegister}
      isTableLoading={isLoadingDrawingRegister}
      searchFilter={
        <SearchFilter
          fieldConfigs={drawingRegisterSearchFieldConfigs}
          onFilterChanged={(filter) => {
            setLocalFilter(filter);
          }}
          defaultFilter={localFilter}
        />
      }
      appliedSearchFilter={localFilter}
      noContentLabel="No Document Register created yet"
      onRenderTable={() => {
        return (
          <>
            <ApiErrorDisplay errors={[{ error: errorDrawingRegister, genericError: 'Get drawing register failed' }]} />
            <Paginator
              totalCount={dataDrawingRegister?.totalCount ?? 0}
              toPage={(pageIndex: number) => setPageNumber(pageIndex + 1)}
              changePageSize={(newSize) => setPageSize(newSize)}
              maxPrevOptions={2}
              maxNextOptions={2}
              pageSizeOptions={DEFAULT_TABLE_PAGE_SIZE_OPTIONS}
              pageIndex={pageNumber - 1}
              pageSize={pageSize}
              pageCount={dataDrawingRegister?.totalPage ?? 0}
              resetPageIndex={() => setPageNumber(1)}
              toNextPage={() => setPageNumber((old) => getValidPageIndex(old + 1, dataDrawingRegister?.totalPage ?? 0))}
              toPrevPage={() => setPageNumber((old) => getValidPageIndex(old - 1, dataDrawingRegister?.totalPage ?? 0))}
              hasPrevPage={pageNumber > 1}
              hasNextPage={pageNumber < (dataDrawingRegister?.totalPage ?? 0)}
              isValidPage={(toPageIndex: number) =>
                toPageIndex >= 0 && toPageIndex < (dataDrawingRegister?.totalPage ?? 0)
              }
            />
            <Grid
              container
              sx={{ display: { xs: 'none', sm: 'flex' } }}
              className="gcl-gems-document-register-t1-accordion-header"
            >
              <Grid item xs={12} sm={4}>
                T1 Ref. No.
              </Grid>
              <Grid item xs={12} sm={7}>
                Design Package
              </Grid>
              <Grid item xs={12} sm={1}>
                Risk Category
              </Grid>
            </Grid>
            <Accordion
              allowClickHeaderToExpand
              className="gcl-gems-document-register-accordions"
              shouldRenderBodyAfterExpanded
              content={(dataDrawingRegister?.items ?? []).map((item) => {
                return {
                  id: item.id,
                  defaultIsExpanded: false,
                  onRenderBody: () => {
                    return <DocumentRegisterT1AccordionContent masterScheduleId={item.id} />;
                  },
                  onRenderHeader: () => {
                    return (
                      <Grid container>
                        <Grid item xs={12} sm={4}>
                          {createTableLinkWithOpenNewTabElement({
                            linkUrl: generateRoutePath(routes.t1.view.path, { id: item.id.toString() }),
                            linkDisplay: item.refNo,
                          })}
                        </Grid>
                        <Grid item xs={12} sm={7}>
                          {item.designPackage}
                        </Grid>
                        <Grid item xs={12} sm={1}>
                          {item.riskCategory}
                        </Grid>
                      </Grid>
                    );
                  },
                };
              })}
            />
          </>
        );
      }}
    />
  );
};
